/**
 * Cv Domain Definition (domain-def@4) — the durable, versioned proof layer.
 *
 * `Cv` (domain-model.md §3.3) — a JSON-Resume superset + schema.org Person
 * JSON-LD. Authored once and **versioned**, NEVER mutated per application
 * (law C6): per-application emphasis lives in `CvTailoring` on the Application, a
 * VIEW over this durable Cv. The section-name vocabulary is the shared
 * `CvSectionName` primitive (kills the old `CvSectionName`/`TailoringSectionName`
 * mirror).
 *
 * Stream: `Agg::career::{userId}::Cv`.
 */

import type { DomainDefinitionJSON } from "./schema"

const cvDomain = {
  schemaVersion: "domain-def@4",
  name: "Cv",
  version: "1.0.0",
  description:
    "Cv — durable, versioned structured proof layer; a JSON Resume superset with schema.org Person JSON-LD mapping and per-entry evidence-claim links",

  types: {
    // ─── JSON Resume value objects (extended) ────────────────────

    CvLocation: {
      "+": "reject",
      "address?": "string",
      "postalCode?": "string",
      "city?": "string",
      "countryCode?": "string",
      "region?": "string",
    },

    CvProfileLink: {
      "+": "reject",
      network: "NonEmptyString",
      "username?": "string",
      "url?": "string",
    },

    CvBasics: {
      "+": "reject",
      name: "NonEmptyString",
      "label?": "string",
      /** Portrait lives in the object store */
      "image?": "ObjectRef | null",
      "email?": "string",
      "phone?": "string",
      "url?": "string",
      "summary?": "string",
      "location?": "CvLocation",
      profiles: "CvProfileLink[]",
    },

    CvWork: {
      "+": "reject",
      name: "NonEmptyString",
      "position?": "string",
      "url?": "string",
      "startDate?": "string",
      "endDate?": "string",
      "summary?": "string",
      highlights: "string[]",
      "evidenceClaims?": "Urn[]",
    },

    CvVolunteer: {
      "+": "reject",
      organization: "NonEmptyString",
      "position?": "string",
      "url?": "string",
      "startDate?": "string",
      "endDate?": "string",
      "summary?": "string",
      highlights: "string[]",
      "evidenceClaims?": "Urn[]",
    },

    CvEducation: {
      "+": "reject",
      institution: "NonEmptyString",
      "url?": "string",
      "area?": "string",
      "studyType?": "string",
      "startDate?": "string",
      "endDate?": "string",
      "score?": "string",
      courses: "string[]",
      "evidenceClaims?": "Urn[]",
    },

    CvAward: {
      "+": "reject",
      title: "NonEmptyString",
      "date?": "string",
      "awarder?": "string",
      "summary?": "string",
      "evidenceClaims?": "Urn[]",
    },

    CvCertificate: {
      "+": "reject",
      name: "NonEmptyString",
      "date?": "string",
      "issuer?": "string",
      "url?": "string",
      /** Link to a stored Asset; path to W3C Verifiable Credentials */
      "asset?": "Urn | null",
      "evidenceClaims?": "Urn[]",
    },

    CvPublication: {
      "+": "reject",
      name: "NonEmptyString",
      "publisher?": "string",
      "releaseDate?": "string",
      "url?": "string",
      "summary?": "string",
      "evidenceClaims?": "Urn[]",
    },

    CvSkill: {
      "+": "reject",
      name: "NonEmptyString",
      "level?": "string",
      keywords: "string[]",
      "evidenceClaims?": "Urn[]",
    },

    CvLanguage: {
      "+": "reject",
      language: "NonEmptyString",
      "fluency?": "string",
      "evidenceClaims?": "Urn[]",
    },

    CvInterest: {
      "+": "reject",
      name: "NonEmptyString",
      keywords: "string[]",
    },

    CvReference: {
      "+": "reject",
      name: "NonEmptyString",
      "reference?": "string",
      "asset?": "Urn | null",
      "evidenceClaims?": "Urn[]",
    },

    CvProject: {
      "+": "reject",
      name: "NonEmptyString",
      "description?": "string",
      highlights: "string[]",
      keywords: "string[]",
      "startDate?": "string",
      "endDate?": "string",
      "url?": "string",
      roles: "string[]",
      "entity?": "string",
      "type?": "string",
      "evidenceClaims?": "Urn[]",
    },

    // ─── Career-studio extensions ────────────────────────────────

    /** Per-section presentation metadata (title override, visibility, order) */
    CvSectionMeta: {
      "+": "reject",
      "title?": "string",
      "visible?": "boolean",
      "order?": "number >= 0",
    },

    /** Per-section metadata keyed by JSON-Resume section name */
    CvSectionMetaMap: {
      "+": "reject",
      "basics?": "CvSectionMeta",
      "work?": "CvSectionMeta",
      "volunteer?": "CvSectionMeta",
      "education?": "CvSectionMeta",
      "awards?": "CvSectionMeta",
      "certificates?": "CvSectionMeta",
      "publications?": "CvSectionMeta",
      "skills?": "CvSectionMeta",
      "languages?": "CvSectionMeta",
      "interests?": "CvSectionMeta",
      "references?": "CvSectionMeta",
      "projects?": "CvSectionMeta",
    },

    Cv: {
      "+": "reject",
      id: "Urn",
      owner: "UserId",
      /** Monotonic version — Cv is durable and versioned */
      version: "number.integer >= 1",
      locale: "Locale",
      "voiceProfile?": "VoiceProfile | null",
      basics: "CvBasics",
      work: "CvWork[]",
      volunteer: "CvVolunteer[]",
      education: "CvEducation[]",
      awards: "CvAward[]",
      certificates: "CvCertificate[]",
      publications: "CvPublication[]",
      skills: "CvSkill[]",
      languages: "CvLanguage[]",
      interests: "CvInterest[]",
      references: "CvReference[]",
      projects: "CvProject[]",
      "sectionMeta?": "CvSectionMetaMap",
      /** schema.org Person JSON-LD mapping for semantic-web interop */
      "jsonLd?": { "[string]": "unknown" },
      createdAt: "Timestamp",
      updatedAt: "Timestamp",
    },

    // ─── Event payloads ──────────────────────────────────────────

    /** Ingest produces a first draft Cv from the raw bundle (awaiting user version) */
    CvDraftedPayload: {
      "+": "reject",
      owner: "UserId",
      cv: "Cv",
      "prov?": "ProvenanceBundle",
    },

    /**
     * What the user actually accepted (and rejected) when approving an ingest
     * proposal. Entry keys are the proposal's stable review keys — a list position
     * (`work#0`) or a `basics` field (`basics.summary`).
     */
    CvProposalReview: {
      "+": "reject",
      accepted: "string[]",
      /** Recorded so the audit stays honest about what the user dropped. */
      rejected: "string[]",
      /** When the reviewed proposal was drafted (the `cv.Drafted` occurredAt). */
      "proposedAt?": "string",
    },

    /** A new durable Cv version was authored — full snapshot; exports render versions */
    CvVersionedPayload: {
      "+": "reject",
      owner: "UserId",
      cvId: "Urn",
      version: "number.integer >= 1",
      cv: "Cv",
      /**
       * ADDITIVE + OPTIONAL: present when this version came from approving an ingest
       * proposal. Absent on a version authored straight from the workbench — folds
       * tolerate its absence, so every pre-existing `cv.Versioned` replays unchanged.
       */
      "review?": "CvProposalReview",
    },

    /** A Cv entry was linked to evidence-ledger claims (per-entry provenance) */
    CvEntryEvidenceLinkedPayload: {
      "+": "reject",
      owner: "UserId",
      cvId: "Urn",
      section: "CvSectionName",
      entryId: "NonEmptyString",
      claimRefs: "Urn[]",
    },

    /** The portrait photo (object-store ref) on `CvBasics.image` was set */
    CvPortraitSetPayload: {
      "+": "reject",
      owner: "UserId",
      cvId: "Urn",
      image: "ObjectRef",
    },
  },

  resources: {
    cv: {
      description: "The calling user's durable, versioned Cv (UI label: Lebenslauf)",
      type: "Cv",
      urnPattern: "urn:career:cv:{userId}",
      fetch: { method: "GET", urlTemplate: "/api/v1/users/{userId}/cv" },
      authz: { visibility: "user-owned", scopes: ["cv:read"] },
    },
  },

  eventStream: {
    streamPattern: "Agg::career::{userId}::Cv",
    events: {
      "cv.Drafted": {
        description: "Ingest produced a first draft structured Cv, awaiting the user's version",
        type: "CvDraftedPayload",
      },
      "cv.Versioned": {
        description: "A new durable Cv version was authored (the Cv is versioned, not per-application)",
        type: "CvVersionedPayload",
      },
      "cv.EntryEvidenceLinked": {
        description: "A Cv entry was linked to one or more evidence-ledger claims",
        type: "CvEntryEvidenceLinkedPayload",
      },
      "cv.PortraitSet": {
        description: "The portrait photo on the Cv basics was set from an uploaded portrait asset",
        type: "CvPortraitSetPayload",
      },
    },
  },

  authz: {
    scopes: {
      "cv:read": { description: "Read the owner's Cv" },
      "cv:write": { description: "Draft, version, or link entries on the owner's Cv" },
    },
    policies: {
      "owner-only": {
        kind: "owner",
        description: "The Cv is owner-scoped to the calling Subject (law C1)",
      },
    },
    defaults: {
      unauthenticatedAccess: "deny",
      missingRolesBehavior: "deny",
    },
  },
} as const satisfies DomainDefinitionJSON

export default cvDomain
